
import React from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { useTranslations } from "use-intl";
import { useFormatOptionalDate } from "@/i18n/useFormatOptionalDate";
import { cn } from "@/lib/utils";

interface PaymentDateButtonProps {
  paymentDate: string;
  dueDate: string;
  isLate: boolean;
  isHolidayAdjusted?: boolean;
  updatePaymentDate: (date: Date) => void;
  disablePastDates: (date: Date) => boolean;
  getPaymentDateObject: () => Date;
}

const PaymentDateButton: React.FC<PaymentDateButtonProps> = ({
  paymentDate,
  dueDate,
  isLate,
  isHolidayAdjusted,
  updatePaymentDate,
  disablePastDates,
  getPaymentDateObject
}) => {
  const t = useTranslations("consultantPayables.paymentDate");
  const formatOptionalDate = useFormatOptionalDate();
  return (
    <div className="flex flex-col">
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="outline"
            size="sm"
            className={cn(
              "flex items-center justify-start gap-2 font-normal",
              isLate && "text-destructive border-destructive"
            )}
          >
            {formatOptionalDate(paymentDate, t("chooseDate"))}
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="single"
            selected={getPaymentDateObject()}
            onSelect={(date) => {
              if (date) {
                // Pass the date directly without any timezone adjustments
                updatePaymentDate(date);
              }
            }}
            disabled={disablePastDates}
            autoFocus
            className="p-3 pointer-events-auto"
          />
          {isLate && (
            <div className="p-2 text-xs text-destructive font-medium bg-destructive/10">
              {t("lateWarning")}
            </div>
          )}
        </PopoverContent>
      </Popover>
      
      {isHolidayAdjusted && (
        <div className="text-xs text-muted-foreground mt-1">
          {t("holidayAdjusted")}
        </div>
      )}
    </div>
  );
};

export default PaymentDateButton;
